import React, { useState, useCallback, useRef } from "react";
import {
  Button,
  Dropdown,
  DropdownList,
  DropdownListItem,
  type ButtonIconComponent,
} from "@sumprio/ui";
import { SafeTooltipTrigger as TooltipTrigger } from "components/common/SafeTooltipTrigger";
import { DotsVerticalIcon } from "@sumprio/ui/icons";
import {
  useListTableRowActionsPlacement,
  type ListTableRowActionsPlacement,
} from "../useListTableRowActionsPlacement";
import { useListTableScrollBoundaryRef } from "../ListTableScrollBoundaryContext";
import "./TableRowIconMenu.scss";

export type TableRowIconMenuIcon = ButtonIconComponent;

export interface TableRowIconMenuItem {
  id: string;
  label: string;
  icon: TableRowIconMenuIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface TableRowIconMenuProps {
  triggerDisabled?: boolean;
  tooltip?: string;
  disabledTooltip?: string;
  ariaLabel?: string;
  items: TableRowIconMenuItem[];
  openUpward?: boolean;
  fixedPlacement?: "top-start" | "bottom-start" | "top-end" | "bottom-end";
  panelZIndex?: number;
  ignoreScrollBoundary?: boolean;
}

export default function TableRowIconMenu({
  triggerDisabled = false,
  tooltip = "Actions",
  disabledTooltip = "No actions available",
  ariaLabel = "Row actions",
  items,
  openUpward = false,
  fixedPlacement,
  panelZIndex = 100200,
  ignoreScrollBoundary = false,
}: TableRowIconMenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listTableScrollBoundaryRef = useListTableScrollBoundaryRef();
  const estimatedPanelHeightPx = Math.min(420, Math.max(160, items.length * 48 + 24));
  const calculatedPlacement: ListTableRowActionsPlacement = useListTableRowActionsPlacement(
    open,
    anchorRef,
    ignoreScrollBoundary ? null : listTableScrollBoundaryRef,
    estimatedPanelHeightPx
  );
  const effectiveDisabled = triggerDisabled || items.length === 0;
  const close = useCallback(() => setOpen(false), []);
  const disabledTip = triggerDisabled ? disabledTooltip : "No actions available";

  if (effectiveDisabled) {
    return (
      <TooltipTrigger tooltip={disabledTip} placement="top-end" theme="dark">
        <span>
          <Button
            iconOnly
            icon={DotsVerticalIcon}
            variant="brand-tertiaryColor"
            size="xs"
            disabled
            aria-label={ariaLabel}
          />
        </span>
      </TooltipTrigger>
    );
  }

  return (
    <div ref={anchorRef} style={{ display: "inline-flex", verticalAlign: "middle" }}>
      <TooltipTrigger tooltip={tooltip} placement="top-end" theme="dark" disabled={open}>
        <Dropdown
          trigger="icon"
          triggerIcon={DotsVerticalIcon}
          open={open}
          onOpenChange={setOpen}
          placement={fixedPlacement ?? (openUpward ? "top-start" : calculatedPlacement)}
          panelZIndex={panelZIndex}
          panelWidth="max-content"
          showTriggerChevron={false}
          portal
        >
          <DropdownList>
            {items.map((item) => {
              const listItem = (
                <div className={item.danger ? "table-row-icon-menu__danger-item" : undefined}>
                  <DropdownListItem
                    type="icon"
                    icon={item.icon}
                    label={
                      item.danger ? (
                        <span className="table-row-icon-menu__danger-label">{item.label}</span>
                      ) : (
                        item.label
                      )
                    }
                    {...(item.disabled ? { state: "disabled" as const } : {})}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (item.disabled) return;
                      close();
                      item.onSelect();
                    }}
                  />
                </div>
              );
              if (item.disabled && item.disabledTooltip) {
                return (
                  <TooltipTrigger
                    key={item.id}
                    tooltip={item.disabledTooltip}
                    placement="left"
                    theme="dark"
                    className="table-row-icon-menu__disabled-item-wrap"
                  >
                    {listItem}
                  </TooltipTrigger>
                );
              }
              return <React.Fragment key={item.id}>{listItem}</React.Fragment>;
            })}
          </DropdownList>
        </Dropdown>
      </TooltipTrigger>
    </div>
  );
}
